import { useState } from "react";
import { getThemeClasses } from "../../utils/ThemeClasses";
import {
  useReportingUnits,
  type ReportingUnit,
} from "../../features/dashboards/reporting";
import type { ReportingPlatform } from "../../features/dashboards/reportingConfig";

const ITEMS_PER_PAGE = 10;

function formatTs(ts: number | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("es-MX");
}

function getPageNumbers(totalPages: number, currentPage: number) {
  const pages: (number | string)[] = [];
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else if (currentPage <= 3) {
    pages.push(1, 2, 3, 4, "...", totalPages);
  } else if (currentPage >= totalPages - 2) {
    pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
  } else {
    pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
  }
  return pages;
}

function UnitsTable({
  title,
  units,
  loading,
  accent,
  themeClasses,
}: {
  title: string;
  units: ReportingUnit[];
  loading: boolean;
  accent: string;
  themeClasses: ReturnType<typeof getThemeClasses>;
}) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const filtered = q
    ? units.filter(
        (u) =>
          (u.imei || "").toLowerCase().includes(q) ||
          (u.user_name || "").toLowerCase().includes(q) ||
          (u.business_name || "").toLowerCase().includes(q),
      )
    : units;
  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const current = Math.min(page, totalPages);
  const slice = filtered.slice(
    (current - 1) * ITEMS_PER_PAGE,
    current * ITEMS_PER_PAGE,
  );

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
        <h3 className={`text-lg font-bold ${themeClasses.text}`}>{title}</h3>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar por IMEI, usuario o razón social…"
            className={`w-full sm:w-64 px-3 py-2 text-sm rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text} focus:outline-none`}
          />
          <span className={`text-sm font-semibold ${accent} whitespace-nowrap`}>
            {q ? `${filtered.length}/${units.length}` : units.length}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
          <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
          <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
        </div>
      ) : units.length === 0 ? (
        <p className={`text-sm ${themeClasses.textSecondary}`}>Sin unidades.</p>
      ) : filtered.length === 0 ? (
        <p className={`text-sm ${themeClasses.textSecondary}`}>Sin resultados.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${themeClasses.cardBorder}`}>
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>IMEI</th>
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Usuario</th>
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Razón social</th>
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Última señal</th>
                </tr>
              </thead>
              <tbody>
                {slice.map((u, i) => (
                  <tr key={`${u.imei}-${i}`} className={`border-b ${themeClasses.cardBorder}`}>
                    <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{u.imei}</td>
                    <td className={`py-2 px-3 ${themeClasses.text}`}>{u.user_name || "—"}</td>
                    <td className={`py-2 px-3 ${themeClasses.text}`}>{u.business_name || "—"}</td>
                    <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{formatTs(u.last_ts)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center mt-6 space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={current === 1}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
              >
                Anterior
              </button>
              {getPageNumbers(totalPages, current).map((p, idx) => (
                <button
                  key={idx}
                  onClick={() => typeof p === "number" && setPage(p)}
                  disabled={p === "..."}
                  className={`px-3 py-1 rounded ${
                    p === current
                      ? "bg-primary-500 text-white"
                      : p === "..."
                        ? "bg-gray-200 text-gray-400 cursor-default"
                        : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={current === totalPages}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ReportingUnitsTables({
  platform,
  themeClasses,
}: {
  platform: ReportingPlatform;
  themeClasses: ReturnType<typeof getThemeClasses>;
}) {
  const notReportingQuery = useReportingUnits({ platform, reporting: "no" });
  const reportingQuery = useReportingUnits({ platform, reporting: "yes" });

  const meta = notReportingQuery.data ?? reportingQuery.data;
  const notReporting = notReportingQuery.data?.units ?? [];
  const reporting = reportingQuery.data?.units ?? [];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            Datos en tiempo real
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            Operatividad del servicio — últimas 24 horas
          </p>
        </div>
        <span
          title="Dato en vivo de reporting/summary (últimas 24 h)."
          className={`text-[11px] whitespace-nowrap inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${themeClasses.cardBorder} ${themeClasses.textSecondary}`}
        >
          <i className="ri-broadcast-line" />
          En vivo · últimas 24 h
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-3 sm:p-4`}>
          <p className={`text-xs ${themeClasses.textSecondary}`}>Reportando</p>
          <p className="text-lg sm:text-2xl font-bold text-green-500">
            {meta?.reporting_count ?? 0}
          </p>
        </div>
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-3 sm:p-4`}>
          <p className={`text-xs ${themeClasses.textSecondary}`}>No reportando</p>
          <p className="text-lg sm:text-2xl font-bold text-red-500">
            {meta?.not_reporting_count ?? 0}
          </p>
        </div>
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-3 sm:p-4`}>
          <p className={`text-xs ${themeClasses.textSecondary}`}>Efectividad</p>
          <p className={`text-lg sm:text-2xl font-bold ${themeClasses.text}`}>
            {meta?.effectiveness_pct ?? 0}%
          </p>
        </div>
      </div>

      <UnitsTable
        title="Unidades que no reportan"
        units={notReporting}
        loading={notReportingQuery.isLoading}
        accent="text-red-500"
        themeClasses={themeClasses}
      />
      <UnitsTable
        title="Unidades que reportan"
        units={reporting}
        loading={reportingQuery.isLoading}
        accent="text-green-500"
        themeClasses={themeClasses}
      />
    </div>
  );
}
